import { Fragment } from 'react';

import PlusIcon from '@usas/ui-web/components/icons/PlusIcon';
import HideIcon from '@usas/ui-web/components/icons/HideIcon';
import ShowIcon from '@usas/ui-web/components/icons/ShowIcon';
import StopwatchIcon from '@usas/ui-web/components/icons/StopwatchIcon';
import DeleteIcon from '@usas/ui-web/components/icons/DeleteIcon';
import Icon from '@usas/ui-web/components/icons/Icon';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { formatTimeForDisplay } from '@usas/ui-web/utils/TimesUtils';

import global from '@usas/ui-web/components/GlobalStyle.module.css';

const LargeDetailTableRow = ({ event, showStatusColumn }) => {
  return (
    <tr>
      <td>{event.eventCode || ''}</td>
      <td>{event.eventCompetitionGenderTypeName || ''}</td>
      <td>{event.ageGroupName || ''}</td>
      <td>{event.meetSessionName || ''}</td>
      {showStatusColumn === true && <td>{event.status || ''}</td>}
      <td>{event.entryTime ? formatTimeForDisplay(event.entryTime) : 'NT'} {event.entryTime ? event.qualifyingCourseCode || '' : ''}</td>
      <td>{event.isAlternateEvent === true ? `ALT: ${event.qualifyingEventCode || ''}` : event.isNonConformingTime === true ? `NC: ${event.qualifyingEventCode || ''}` : `${event.qualifyingEventCode || ''}`}</td>
      <td>{event.qualifiedAsBonus === true ? 'Yes' : event.qualifiedAsBonus === false ? 'No' : ''}</td>
      <td>{event.swimTimeEntryOverride === true ? 'Yes' : event.swimTimeEntryOverride === false ? 'No' : ''}</td>
    </tr>
  )
}

const LargeDetailTable = ({ athlete, showStatusColumn }) => {
  return (
    <table className={global.DetailTable}>
      <thead>
        <tr>
          <th style={{ minWidth: '90px' }}>Event</th>
          <th style={{ minWidth: '115px' }}>Comp. Category</th>
          <th style={{ minWidth: '80px' }}>Age Group</th>
          <th>Session</th>
          {showStatusColumn === true &&
            <th>Status</th>}
          <th style={{ minWidth: '100px' }}>Entry Time</th>
          <th style={{ minWidth: '90px' }}>Time Event</th>
          <th>Bonus?</th>
          <th>Override?</th>
        </tr>
      </thead>
      <tbody>
        {Array.isArray(athlete.events) && athlete.events.length > 0 &&
          athlete.events.map((event, i) =>
            <LargeDetailTableRow
              event={event}
              showStatusColumn={showStatusColumn}
              key={i}
            />)}
      </tbody>
    </table>
  );
};

const GridRow = ({ index, athlete, onEditAthleteClicked, onAddSecondaryRepToAthleteClicked, onDeleteAthleteClicked, showSecondaryRep, expandedId, onClick, onUpdateAthleteTimes, showStatusColumn }) => {
  return (
    <Fragment key={athlete.orgUnitAthleteEntryId || index + 'Fragment'}>
      <tr key={athlete.orgUnitAthleteEntryId || index}>
        <td>{(athlete.firstName || athlete.middleName || athlete.lastName) ? `${athlete.firstName || ''} ${athlete.preferredName !== '' && athlete.preferredName !== athlete.firstName ? '"' + athlete.preferredName + '"' : ''} ${athlete.middleName || ''} ${athlete.lastName || ''}` : ''}</td>
        <td>{athlete.competitionGenderTypeName || ''}</td>
        <td>{athlete.birthDate ? formatDate(athlete.birthDate) : ''}</td>
        <td>{athlete.ageAtMeet >= 0 && athlete.ageAtMeet !== null ? athlete.ageAtMeet : ''}</td>
        <td>{athlete.status || ''}</td>
        {showSecondaryRep === true &&
          <td>{athlete.secondaryRepresentationOrgUnitId ? `${athlete.secondaryRepresentationOrgUnitName || ''} (${athlete.secondaryRepresentationParentOrgUnitCode || ''}/${athlete.secondaryRepresentationOrgUnitCode || ''})` : ''}</td>}
        <td>
          {onEditAthleteClicked && athlete.isRelayOnlyAthlete === false &&
            <button className={global.IconButtonMargin}
              type="button"
              onClick={(e) => onEditAthleteClicked(e, athlete)}>
              <PlusIcon toolTipText='Manage Athlete Events' />
            </button>}
          {onAddSecondaryRepToAthleteClicked && showSecondaryRep === true &&
            <button className={global.IconButtonMargin}
              type="button"
              onClick={(e) => onAddSecondaryRepToAthleteClicked(e, athlete)}>
              <Icon tooltip={"Edit Secondary Representation"} iconObj={faEdit} />
            </button>}
          {onDeleteAthleteClicked && (athlete.canBeDeleted === true || (athlete.isRelayAthlete === false && athlete.hasPaid === false)) &&
            <button className={global.IconButtonMargin}
              type="button"
              onClick={(e) => onDeleteAthleteClicked(e, athlete)}>
              <DeleteIcon />
            </button>}
          {Array.isArray(athlete.events) && athlete.events.length > 0 &&
            <Fragment>
              {onUpdateAthleteTimes &&
                <button className={global.IconButtonMargin}
                  type="button"
                  onClick={(e) => onUpdateAthleteTimes(e, athlete)}>
                  <StopwatchIcon toolTipText='Update Individual Event Entry Times for Athlete' />
                </button>}
              <button
                className={global.IconButtonMargin}
                type="button"
                onClick={(e) => onClick(e, athlete.orgUnitAthleteEntryId)}>
                {expandedId === athlete.orgUnitAthleteEntryId ? <HideIcon /> : <ShowIcon />}
              </button>
            </Fragment>
          }
        </td>
      </tr>
      {expandedId === athlete.orgUnitAthleteEntryId && Array.isArray(athlete.events) && athlete.events.length > 0 &&
        <tr className={global.Expanded}>
          <td colSpan={showSecondaryRep === true ? '7' : '6'}>
            <LargeDetailTable athlete={athlete} showStatusColumn={showStatusColumn} />
          </td>
        </tr>}
    </Fragment>
  );
};

const OrgUnitAdminRosterEntriesLargeGrid = ({ gridData, isLoading, onEditAthleteClicked, onAddSecondaryRepToAthleteClicked, onDeleteAthleteClicked,
  showSecondaryRep, expandedId, onClick, onUpdateAthleteTimes, showStatusColumn }) => (
  <table className={[global.UsasTable, 'visible-md visible-lg'].join(' ')}>
    <thead>
      <tr>
        <th>Name</th>
        <th style={{ minWidth: '125px' }}>Comp. Category</th>
        <th>Birth Date</th>
        <th style={{ minWidth: '90px' }}>Age at Meet</th>
        <th>Status</th>
        {showSecondaryRep === true &&
          <th style={{ minWidth: '190px' }}>Secondary Representation</th>}
        <th></th>
      </tr>
    </thead>
    <tbody>
      {isLoading === true ?
        <tr><td colSpan={showSecondaryRep === true ? '7' : '6'}>Loading...</td></tr>
        : Array.isArray(gridData) && gridData.length > 0 ?
          gridData.map((athlete, i) =>
            <GridRow
              key={i}
              index={i}
              athlete={athlete}
              onEditAthleteClicked={onEditAthleteClicked}
              onAddSecondaryRepToAthleteClicked={onAddSecondaryRepToAthleteClicked}
              onDeleteAthleteClicked={onDeleteAthleteClicked}
              showSecondaryRep={showSecondaryRep}
              expandedId={expandedId}
              onClick={onClick}
              onUpdateAthleteTimes={onUpdateAthleteTimes}
              showStatusColumn={showStatusColumn}
            />)
          :
          <tr>
            <td colSpan={showSecondaryRep === true ? '7' : '6'}>No Athletes</td>
          </tr>
      }
    </tbody>
  </table>
);

export default OrgUnitAdminRosterEntriesLargeGrid;